<script lang="ts" setup>import { computed as _computed, ref as _ref } from 'vue';

import { faGift } from '@fortawesome/pro-light-svg-icons'
import { formatCurrency } from '@consumer/utils/currency'
import type { PurchasedOrder, PurchasedOrderGift } from '@consumer/types/serializers'
import OrderStatus from '@consumer/modules/buyer/OrderStatus.vue'
import OrderGiftContentDelivery from '@consumer/modules/buyer/OrderGiftContentDelivery.vue'
import OrderGiftContentGiftDeliveries from '@consumer/modules/buyer/OrderGiftContentGiftDeliveries.vue'
import RedeliverOptionsDialog from '@consumer/modules/buyer/modals/redeliver/RedeliverOptionsDialog.vue'
import { isContact } from '@consumer/utils/types'
import { useSharedData } from '@consumer/composables/sharedData'

withDefaults(defineProps<{
  orderGift: PurchasedOrderGift
  order: PurchasedOrder
  wrapDateLine?: boolean
}>(), {  })

const { currentUser } = useSharedData()

const greeting = _computed(() => __props.orderGift.greeting)
const delivery = _computed(() => __props.orderGift.delivery)

const isViewedByGifter = _computed(() => {
  return __props.orderGift.gifterId === currentUser?.id
})

let isRedeliverOptionsDialogOpen = _ref(false)

function onGiftNotDelivered () {
  isRedeliverOptionsDialogOpen.value = true
}
</script>

<template>
  <div data-testid="order-gift-info" class="flex justify-between flex-1">
    <div class="text-sm">
      <div class="w-full flex flex-col flex-wrap gap-2">
        <div class="flex gap-2 items-center">
          <div data-testid="order-gift-recipient" class="text-lg font-semibold">
            {{ greeting.to ?? 'Gift Recipient' }}
          </div>
          <GBadge
            v-if="delivery.recipient?.usernameWithAt"
            class="text-xs !bg-white
            !border-v2-secondary !border-opacity-50 border-1 !text-v2-secondary font-normal
            !pl-1
          "
            size="sm"
            type="chip"
            :label="delivery.recipient.usernameWithAt"
          >
            <template #pre>
              <ProfilePicture :user="delivery.recipient as any" :size="15" class="rounded-1"/>
            </template>
          </GBadge>
          <GBadge
            v-if="isContact(delivery.recipient) && isViewedByGifter"
            class="text-xs !bg-white
            !border-v2-secondary !border-opacity-50 border-1 !text-v2-secondary font-normal
          "
            size="sm"
            type="chip"
            label="Contact"
          />
        </div>

        <div class="flex gap-2 items-center">
          <V2Icon :icon="faGift" title="Gift" class="stroke-v2-secondary"/>
          <div data-testid="order-gift-amount" class="mt-0.5">
            {{ formatCurrency(orderGift.amount) }}
          </div>
          <div data-testid="order-gift-status">
            <OrderStatus :gift="orderGift"/>
          </div>
        </div>

        <OrderGiftContentGiftDeliveries
          v-if="orderGift.giftDeliveries.length"
          :orderGift="orderGift"
          @giftNotDelivered="onGiftNotDelivered"
        />
        <OrderGiftContentDelivery
          v-else
          :orderGift="orderGift"
          @giftNotDelivered="onGiftNotDelivered"
        />
      </div>
    </div>
  </div>

  <RedeliverOptionsDialog
    v-model:open="isRedeliverOptionsDialogOpen"
    :orderGift="orderGift"
    :order="order"
  />
</template>
